import '@polymer/paper-button/paper-button.js';
import '@polymer/paper-dropdown-menu/paper-dropdown-menu.js';
import '@polymer/paper-item/paper-item.js';
import '@polymer/paper-listbox/paper-listbox.js';

import { css, html, LitElement, property, unsafeCSS } from "lit-element";
import { v4 as uuid } from 'uuid';
import MetricCalculationConfigType from "../../enums/metric-calculation-config-type";
import ReportConfigMetricType from "../../enums/report-config-metric-type";
import { DropdownModel } from "../../models/components/DropdownModel";
import { ReportConfigMetricCalculationConfigModel } from "../../models/report/ReportConfigMetricCalculationConfigModel";
import { ReportConfigMetricCalculationModel } from "../../models/report/ReportConfigMetricCalculationModel";
import { ReportConfigMetricModel } from "../../models/report/ReportConfigMetricModel";
import { ReportConfigModel } from "../../models/report/ReportConfigModel";
import { ReportEventCodeModel } from "../../models/report/ReportEventCodeModel";
import { ReportEventItemTypeOptionModel } from "../../models/report/ReportEventItemTypeOptionModel";
import Dropdown from "../dropdown/dropdown";
import reportConfigViewStyles from './report-config-view.scss';

export default class ReportConfigView extends LitElement {
    @property({ type: Object })
    reportConfig: ReportConfigModel;

    @property({ type: Array })
    eventCodes: ReportEventCodeModel[];

    @property({ type: Array })
    eventItemTypeOptions: ReportEventItemTypeOptionModel[];

    static get styles() {
        return css`${unsafeCSS(reportConfigViewStyles)}`;
    }

    constructor(reportConfig: ReportConfigModel, eventCodes: ReportEventCodeModel[], eventItemTypeOptions: ReportEventItemTypeOptionModel[]) {
        super();

        this.reportConfig = reportConfig;
        this.eventCodes = eventCodes;
        this.eventItemTypeOptions = eventItemTypeOptions;
    }

    getEventCode(eventCodeGuid) {
        this.eventCodes.find((reportEventCode) => reportEventCode.eventCodeGuid == eventCodeGuid);
    }

    getEventItemTypeOption(eventItemTypeOptionGuid) {
        this.eventItemTypeOptions.find((reportEventItemTypeOption) => reportEventItemTypeOption.eventItemTypeOptionGuid == eventItemTypeOptionGuid);
    }

    eventCodeChange(value, metricIndex, eventCodeIndex) {
        this.reportConfig.reportConfigMetrics[metricIndex].eventGuids[eventCodeIndex] = value;
    }

    eventItemTypeOptionChange(value, metricIndex, eventItemTypeOptionIndex) {
        this.reportConfig.reportConfigMetrics[metricIndex].optionGuids[eventItemTypeOptionIndex] = value;
    }

    buildEventCodeSelect(eventCodeGuid, metricIndex, eventCodeIndex) {
        let dropdownItems: DropdownModel[] = [];

        dropdownItems.push(new DropdownModel(undefined, "Please select an Event Code", null));
        for (let [i, eventCode] of Object.entries(this.eventCodes)) {
            dropdownItems.push(new DropdownModel(eventCode.eventCodeGuid, eventCode.name, eventCode));
        }

        let eventCode = this.eventCodes.find((eventCode) => eventCode.eventCodeGuid == eventCodeGuid);

        let selectedValue = eventCode ? eventCode.eventCodeGuid : undefined;

        return new Dropdown(dropdownItems, (value, eventCode) => this.eventCodeChange(value, metricIndex, eventCodeIndex), selectedValue, "Event Codes");
    }

    buildEventItemTypeOptionSelect(eventItemTypeOptionGuid, metricIndex, eventItemTypeOptionIndex) {
        let eventItemTypeOption = this.eventItemTypeOptions.find((reportEventItemType) => reportEventItemType.eventItemTypeOptionGuid == eventItemTypeOptionGuid);

        let dropdownItems: DropdownModel[] = [];

        dropdownItems.push(new DropdownModel(undefined, "Please select an Event Code", null));
        for (let [i, eventItemTypeOption] of Object.entries(this.eventItemTypeOptions)) {
            dropdownItems.push(new DropdownModel(eventItemTypeOption.eventItemTypeOptionGuid, eventItemTypeOption.name, eventItemTypeOption));
        }

        let selectedValue = eventItemTypeOption ? eventItemTypeOption.eventItemTypeOptionGuid : undefined;

        return new Dropdown(dropdownItems, (value, eventCode) => this.eventItemTypeOptionChange(value, metricIndex, eventItemTypeOptionIndex), selectedValue, "Event Codes");
    }

    createMetricTypeDropdown(reportConfigMetric: ReportConfigMetricModel) {
        let dropdownItems: DropdownModel[] = [];

        dropdownItems.push(new DropdownModel(ReportConfigMetricType.Sequence, "Sequence", reportConfigMetric));
        dropdownItems.push(new DropdownModel(ReportConfigMetricType.Single, "Single", reportConfigMetric));
        dropdownItems.push(new DropdownModel(ReportConfigMetricType.OrganicSingle, "Organic Single", reportConfigMetric));
        dropdownItems.push(new DropdownModel(ReportConfigMetricType.OrganicSum, "Organic Sum", reportConfigMetric));

        return new Dropdown(dropdownItems, this.metricTypeChange, reportConfigMetric.type.toString(), 'Metric Type');
    }

    metricTypeChange(value, reportConfigMetric: ReportConfigMetricModel) {
        reportConfigMetric.type = value;
    }

    removeEventItemTypeOption(metricIndex, eventItemTypeOptionIndex) {
        this.reportConfig.reportConfigMetrics[metricIndex].optionGuids.splice(eventItemTypeOptionIndex, 1);
        this.requestUpdate();
    }

    removeEventCode(metricIndex, eventCodeIndex) {
        this.reportConfig.reportConfigMetrics[metricIndex].eventGuids.splice(eventCodeIndex, 1);
        this.requestUpdate();
    }

    addEventCode(reportConfigMetricIndex) {
        this.reportConfig.reportConfigMetrics[reportConfigMetricIndex].eventGuids.push("");
        this.requestUpdate();
    }

    addEventItemTypeOption(reportConfigMetricIndex) {
        this.reportConfig.reportConfigMetrics[reportConfigMetricIndex].optionGuids.push("");
        this.requestUpdate();
    }

    valueChange(value, item, index, key) {
        if (index == undefined) {
            item[key] = value;
        }
        else {
            item[index][key] = value;
        }
    }

    deleteMetric(metricIndex) {
        this.reportConfig.reportConfigMetrics.splice(metricIndex, 1);
        this.requestUpdate();
    }

    deleteMetricCalculation(metricCalculations: ReportConfigMetricCalculationModel[], metricCalculationIndex) {
        metricCalculations.splice(metricCalculationIndex, 1);
        this.requestUpdate();
    }

    toggleIcon(control) {
        if (control.tagName.toLowerCase() == 'i') {
            control = control.parentNode;
        }

        if (control.classList.contains('expanded')) {
            control.classList.remove('expanded');
            control.innerHTML = '<i class="fa fa-chevron-down"></i>';
        } else {
            control.classList.add('expanded');
            control.innerHTML = '<i class="fa fa-chevron-up"></i>';
        }
    }

    buildReportConfig() {
        return html`        
            <div class="row">
                <div class="col">    
                    <label>Name</label>
                    <input type="text" 
                        class="form-control"
                        placeholder="name" 
                        id="report-name" 
                        value="${this.reportConfig.name}" 
                        @change=${(event) => this.valueChange(event.target.value, this.reportConfig, undefined, "name")} />
                </div>
            </div>

            <div class="row">
                <div class="col">    
                    <label>Report Id</label>
                    <input type="text" 
                        class="form-control"
                        placeholder="ReportId" 
                        id="report-id" 
                        value="${this.reportConfig.reportId}" 
                        @change=${(event) => this.valueChange(event.target.value, this.reportConfig, undefined, "reportId")} />
                </div>
            </div>

            <div class="row">
                <div class="col"> 
                    <label>Event Code Type</label>
                    <input type="number" 
                        class="form-control"
                        step="1" 
                        min="0" 
                        max="2" 
                        placeholder="Event Code Type" 
                        id="event-code-type" 
                        value="${this.reportConfig.eventCodeType}" 
                        @change=${(event) => this.valueChange(event.target.value, this.reportConfig, undefined, "eventCodeType")} />
                </div>
            </div>

            <div class="row">
                <div class="col">
                    <h5>Coded Events</h5>
                </div>
            </div>
            
            ${this.reportConfig.reportConfigMetrics.map((reportConfigMetric, metricIndex) => html`                
                    <div class="row">
                        <div class="col-auto align-self-end">
                            <button class="btn btn-warning"  
                                @click=${(event) => this.toggleIcon(event.target)}                                   
                                data-toggle="collapse"                                     
                                data-target="#metrics-collapse-${metricIndex}">
                                    <i class="fa fa-chevron-down"></i>
                                </button>                                        
                        </div>
                        <div class="col">
                            <label>Name</label>
                            <input input="text"
                                class="form-control" 
                                @change=${(event) => this.valueChange(event.target.value, this.reportConfig.reportConfigMetrics, metricIndex, "name")} 
                                value="${reportConfigMetric.name}" />
                        </div>
                        <div class="col-auto align-self-end">                                    
                            <button class="btn btn-danger" @click=${(event) => this.deleteMetric(metricIndex)}>
                                <i class="fa fa-trash"></i>
                            </button>
                        </div>
                    </div>                    
                    <div class="collapse indent" id="metrics-collapse-${metricIndex}">                     
                        
                        <div class="row">
                            <div class="col">
                                <label>Metric Id</label>
                                <input input="text"
                                class="form-control" 
                                @change=${(event) => this.valueChange(event.target.value, this.reportConfig.reportConfigMetrics, metricIndex, "metricId")} 
                                value="${reportConfigMetric.metricId}" />
                            </div>
                        </div>

                        <div class="row">
                            <div class="col">                                
                                ${this.createMetricTypeDropdown(reportConfigMetric)}
                            </div>
                        </div>

                        <div class="row">
                            <div class="col">
                                <h5>Events</h5>
                            </div>
                        </div>

                        <div class="event-codes-container">
                            <div class="row">
                                <div class="col align-self-end">
                                    <button @click=${() => this.addEventCode(metricIndex)} class="btn btn-success w-100">add event code</button>                                    
                                </div>
                            </div>
                            
                            ${reportConfigMetric.eventGuids.map((eventCodeGuid, eventCodeIndex) => html`
                                <div class="row">
                                    <div class="col">
                                        ${this.buildEventCodeSelect(eventCodeGuid, metricIndex, eventCodeIndex)}
                                    </div>
                                    <div class="col-auto align-self-end">
                                        <button @click=${(event) => this.removeEventCode(metricIndex, eventCodeIndex)} class="btn btn-danger">
                                            <i class="fa fa-trash"></i>
                                        </button>
                                    </div>
                                </div>`)}                        
                        </div>

                        <div class="row">
                            <div class="col">
                                <h5>Options</h5>
                            </div>
                        </div>
                        
                        <div class="event-item-type-options-container">                                                    
                            <div class="row">
                                <div class="col">
                                    <button @click=${() => this.addEventItemTypeOption(metricIndex)} class="btn btn-success w-100">add event item type option</button>
                                </div>
                            </div>

                            ${reportConfigMetric.optionGuids.map((eventItemTypeOptionGuid, eventItemTypeOptionIndex) => html`
                                <div class="row">
                                    <div class="col">
                                        ${this.buildEventItemTypeOptionSelect(eventItemTypeOptionGuid, metricIndex, eventItemTypeOptionIndex)}
                                    </div>
                                    <div class="col align-self-end">
                                        <button @click=${(event) => this.removeEventItemTypeOption(metricIndex, eventItemTypeOptionIndex)} class="btn btn-danger">
                                            <i class="fa fa-trash"></i>
                                        </button>
                                    </div>
                                </div>`)}                                    
                        </div>
                    </div>`)}

            <div class="row"
                <div class="col">
                    <button @click=${() => this.addReportConfigMetric()} class="btn btn-success w-100">Add Coded Event</button>    
                </div>
            </div>
            
            <div class="row">
                <div class="col">
                    <h5>Calculations</h5>
                </div>
            </div>

            ${this.reportConfig.metricCalculations.map((metricCalculation, calculationIndex) => html`
            <div class="report-config-metric-container">
                <div class="report-config-metric-title">   
                    <div class="row">  
                        <div class="col-auto align-self-end">                   
                            <button class="btn btn-warning" 
                                @click=${(event) => this.toggleIcon(event.target)}
                                data-toggle="collapse" 
                                data-target="#metric-calculations-${calculationIndex}">
                                <i class="fa fa-chevron-down"></i>
                            </button>
                        </div>
                        <div class="col">                        
                            <label>Metric Calculation Name</label>
                            <input input="text" 
                                class="form-control"
                                @change=${(event) => this.valueChange(event.target.value, this.reportConfig.metricCalculations, calculationIndex, "name")} 
                                value="${metricCalculation.name}" />
                        </div>
                        <div class="col-auto align-self-end">
                            <button class="btn btn-danger" @click=${(event) => this.deleteMetricCalculation(this.reportConfig.metricCalculations, calculationIndex)}>
                                <i class="fa fa-trash"></i>
                            </button>
                        </div>
                    </div>
                </div>
                <div class="collapse indent report-config-metric-events" id="metric-calculations-${calculationIndex}">
                    ${metricCalculation.metricCalculationConfigs.sort((a, b) => a.order - b.order).map((metricCalculationConfig, configIndex) => html`
                        <div class="row">
                            <div class="col">
                                ${this.createCalulationDropdown(metricCalculationConfig)}   
                            </div>
                            <div class="col-auto align-self-end">
                                <button @click=${() => this.sortMetricCalculation(metricCalculation.metricCalculationConfigs, metricCalculationConfig, true)} class="btn btn-info">
                                    <i class="fa fa-chevron-down"></i>
                                </button>   
                            </div>
                            <div class="col-auto align-self-end">
                                <button @click=${() => this.sortMetricCalculation(metricCalculation.metricCalculationConfigs, metricCalculationConfig, false)} class="btn btn-info">
                                    <i class="fa fa-chevron-up"></i>
                                </button>   
                            </div>
                            <div class="col-auto align-self-end">
                                <button @click=${() => this.removeMetricCalculation(this.reportConfig.metricCalculations, calculationIndex, configIndex)} class="btn btn-danger">
                                    <i class="fa fa-trash"></i>
                                </button>   
                            </div>
                        </div>
                    `)}
                    <div class="row">
                        <div class="col">
                            <button @click=${() => this.addMetricCalculationConfig(this.reportConfig.metricCalculations, calculationIndex)} class="btn btn-success">add calculation</button>
                        </div>
                    </div>
                </div>
            </div>`)}

            <div class="row">
                <div class="col">
                    <button @click=${() => this.addMetricCalculation(this.reportConfig.metricCalculations)} class="btn btn-success w-100">Add metric calculation</button>
                </div>
            </div>

            <div class="row">
                <div class="col">
                    <h5>Second Calculations</h5>
                </div>            
            </div>            
            
            ${this.reportConfig.secondMetricCalculations.map((metricCalculation, calculationIndex) => html`
            <div class="report-config-metric-container">
                <div class="report-config-metric-title">   
                    <div class="row">  
                        <div class="col-auto align-self-end">                   
                            <button class="btn btn-warning" 
                                @click=${(event) => this.toggleIcon(event.target)}
                                data-toggle="collapse" 
                                data-target="#second-metric-calculations-${calculationIndex}">
                                <i class="fa fa-chevron-down"></i>
                            </button>
                        </div>
                        <div class="col">                        
                            <label>Metric Calculation Name</label>
                            <input input="text" 
                                class="form-control"
                                @change=${(event) => this.valueChange(event.target.value, this.reportConfig.secondMetricCalculations, calculationIndex, "name")} 
                                value="${metricCalculation.name}" />
                        </div>
                        <div class="col-auto align-self-end">
                            <button class="btn btn-danger" @click=${(event) => this.deleteMetricCalculation(this.reportConfig.secondMetricCalculations, calculationIndex)}>
                                <i class="fa fa-trash"></i>
                            </button>
                        </div>
                    </div>
                </div>
                <div class="collapse indent report-config-metric-events" id="second-metric-calculations-${calculationIndex}">
                    ${metricCalculation.metricCalculationConfigs.sort((a, b) => a.order - b.order).map((metricCalculationConfig, configIndex) => html`
                        <div class="row">
                            <div class="col">
                                ${this.createSecondCalculationDropdown(metricCalculationConfig)}   
                            </div>
                            <div class="col-auto align-self-end">
                                <button @click=${() => this.sortMetricCalculation(metricCalculation.metricCalculationConfigs, metricCalculationConfig, true)} class="btn btn-info">
                                    <i class="fa fa-chevron-down"></i>
                                </button>   
                            </div>
                            <div class="col-auto align-self-end">
                                <button @click=${() => this.sortMetricCalculation(metricCalculation.metricCalculationConfigs, metricCalculationConfig, false)} class="btn btn-info">
                                    <i class="fa fa-chevron-up"></i>
                                </button>   
                            </div>
                            <div class="col-auto align-self-end">
                                <button @click=${() => this.removeMetricCalculation(this.reportConfig.secondMetricCalculations, calculationIndex, configIndex)} class="btn btn-danger">
                                    <i class="fa fa-trash"></i>
                                </button>   
                            </div>
                        </div>
                    `)}
                    <div class="row">
                        <div class="col">
                            <button @click=${() => this.addMetricCalculationConfig(this.reportConfig.secondMetricCalculations, calculationIndex)} class="btn btn-success">add calculation</button>
                        </div>
                    </div>
                </div>
            </div>`)}

            <div class="row">
                <div class="col">
                    <button @click=${() => this.addMetricCalculation(this.reportConfig.secondMetricCalculations)} class="btn btn-success w-100">Add metric calculation</button>
                </div>
            </div>`
            
            ;
    }

    addReportConfigMetric() {
        this.reportConfig.reportConfigMetrics.push(<ReportConfigMetricModel>{
            metricId: uuid(),
            name: "",
            type: 0,
            eventGuids: [],
            optionGuids: []
        });

        this.requestUpdate();
    }

    addMetricCalculation(metricCalculations: ReportConfigMetricCalculationModel[]) {
        metricCalculations.push(<ReportConfigMetricCalculationModel>{
            metricId: uuid(),
            name: "",
            metricCalculationConfigs: []
        });

        this.requestUpdate();
    }

    sortMetricCalculation(configs: ReportConfigMetricCalculationConfigModel[], calculationConfig: ReportConfigMetricCalculationConfigModel, sortUp: boolean) {

        if (sortUp) {

            let configIndex = configs.indexOf(calculationConfig);
            if (configs[configIndex + 1]) {
                configs[configIndex + 1].order--;
                calculationConfig.order++;

                this.requestUpdate();
            }

        } else {

            let configIndex = configs.indexOf(calculationConfig);
            if (configs[configIndex - 1]) {
                configs[configIndex - 1].order++;
                calculationConfig.order--;

                this.requestUpdate();
            }

        }
    }

    removeMetricCalculation(metricCalculations: ReportConfigMetricCalculationModel[], metricCalculationIndex: number, configIndex: number) {
        metricCalculations[metricCalculationIndex].metricCalculationConfigs.splice(configIndex, 1);
        this.requestUpdate();
    }

    addMetricCalculationConfig(metricCalculations: ReportConfigMetricCalculationModel[], metricCalculationIndex: number) {
        let configLength = metricCalculations[metricCalculationIndex].metricCalculationConfigs.length;

        let highestOrder = 0;
        let type = 0;
        if (configLength) {
            let previousCalculationConfig = metricCalculations[metricCalculationIndex].metricCalculationConfigs[configLength - 1];

            type = previousCalculationConfig.type == MetricCalculationConfigType.NumberMetric ? MetricCalculationConfigType.OperatorAdd : MetricCalculationConfigType.NumberMetric;

            highestOrder = previousCalculationConfig.order;
        }

        metricCalculations[metricCalculationIndex].metricCalculationConfigs.push(<ReportConfigMetricCalculationConfigModel>{
            metricId: uuid(),
            name: '',
            order: highestOrder + 1,
            type: type
        });

        this.requestUpdate();
    }

    createCalulationDropdown(metricCalculationConfig: ReportConfigMetricCalculationConfigModel) {
        let dropdownControl: Dropdown;
        let items: DropdownModel[] = [];
        let selectedValue = null;
        let label = ""

        switch (metricCalculationConfig.type) {
            case MetricCalculationConfigType.NumberMetric: {

                selectedValue = metricCalculationConfig.metricId;
                for (let [index, reportConfigMetric] of Object.entries(this.reportConfig.reportConfigMetrics)) {
                    items.push(new DropdownModel(reportConfigMetric.metricId, reportConfigMetric.name, metricCalculationConfig));
                }

                label = "Metric";

                dropdownControl = new Dropdown(items, this.metricSelected, selectedValue, label);
                break;
            }
            case MetricCalculationConfigType.OperatorAdd:
            case MetricCalculationConfigType.OperatorDivide:
            case MetricCalculationConfigType.OperatorMinus:
            case MetricCalculationConfigType.OperatorMultiply:

                items.push(new DropdownModel(MetricCalculationConfigType.OperatorAdd, "Add", metricCalculationConfig));
                items.push(new DropdownModel(MetricCalculationConfigType.OperatorDivide, "Divide", metricCalculationConfig));
                items.push(new DropdownModel(MetricCalculationConfigType.OperatorMinus, "Minus", metricCalculationConfig));
                items.push(new DropdownModel(MetricCalculationConfigType.OperatorMultiply, "Multiply", metricCalculationConfig));

                selectedValue = metricCalculationConfig.type;

                label = "Operator";

                dropdownControl = new Dropdown(items, this.operatorSelected, selectedValue, label);
                break;
        }

        return dropdownControl;
    }

    createSecondCalculationDropdown(metricCalculationConfig: ReportConfigMetricCalculationConfigModel) {
        let dropdownControl: Dropdown;
        let items: DropdownModel[] = [];
        let selectedValue = null;
        let label = ""

        switch (metricCalculationConfig.type) {
            case MetricCalculationConfigType.NumberMetric: {

                selectedValue = metricCalculationConfig.metricId;
                for (let [index, reportConfigMetric] of Object.entries(this.reportConfig.reportConfigMetrics)) {
                    items.push(new DropdownModel(reportConfigMetric.metricId, reportConfigMetric.name, metricCalculationConfig));
                }

                //Also add previous calculated metrics
                for (let [index, reportConfigCalculateMetric] of Object.entries(this.reportConfig.metricCalculations)) {
                    items.push(new DropdownModel(reportConfigCalculateMetric.metricId, reportConfigCalculateMetric.name, metricCalculationConfig));
                }

                label = "Metric";

                dropdownControl = new Dropdown(items, this.metricSelected, selectedValue, label);
                break;
            }
            case MetricCalculationConfigType.OperatorAdd:
            case MetricCalculationConfigType.OperatorDivide:
            case MetricCalculationConfigType.OperatorMinus:
            case MetricCalculationConfigType.OperatorMultiply:

                items.push(new DropdownModel(MetricCalculationConfigType.OperatorAdd, "Add", metricCalculationConfig));
                items.push(new DropdownModel(MetricCalculationConfigType.OperatorDivide, "Divide", metricCalculationConfig));
                items.push(new DropdownModel(MetricCalculationConfigType.OperatorMinus, "Minus", metricCalculationConfig));
                items.push(new DropdownModel(MetricCalculationConfigType.OperatorMultiply, "Multiply", metricCalculationConfig));

                selectedValue = metricCalculationConfig.type;

                label = "Operator";

                dropdownControl = new Dropdown(items, this.operatorSelected, selectedValue, label);
                break;
        }

        return dropdownControl;
    }

    operatorSelected = async (value, metricCalculationConfig: ReportConfigMetricCalculationConfigModel) => {
        metricCalculationConfig.type = value;

        switch (metricCalculationConfig.type) {
            case MetricCalculationConfigType.OperatorAdd: {
                metricCalculationConfig.name = "Add";
                break;
            }
            case MetricCalculationConfigType.OperatorDivide: {
                metricCalculationConfig.name = "Divide";
                break;
            }
            case MetricCalculationConfigType.OperatorMinus: {
                metricCalculationConfig.name = "Minus";
                break;
            }
            case MetricCalculationConfigType.OperatorMultiply: {
                metricCalculationConfig.name = "Multiply";
                break;
            }
        }
    }

    metricSelected = async (value, metricCalculationConfig: ReportConfigMetricCalculationConfigModel) => {
        metricCalculationConfig.metricId = value;
    }

    createRenderRoot() {
        return this;
    }

    render() {
        return [html`${this.reportConfig ? this.buildReportConfig() : ''} `];
    }
}

window.customElements.define('report-config-view', ReportConfigView as any);